import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Qué entidad está viendo el admin, para que el asistente entienda "este
// proceso" o "este cliente". Cada pantalla de detalle la informa con
// useContextoAsistente; el overlay la lee con usePantallaActual.
//
// Las pantallas anidadas registran las dos: dentro del proceso, la actividad
// también se informa. React corre los efectos del hijo antes que los del
// padre, así que el orden de registro no sirve para desempatar: gana la
// entidad más específica según PRIORIDAD.
const PRIORIDAD = {
  cliente: 0,
  proceso: 1,
  actividad: 2,
  certificado: 2,
  propuesta: 2,
  solicitud: 2,
  muestra: 2
};

const ContextoAsistente = createContext(null);

// Ancho del panel lateral del asistente en desktop (en mobile ocupa toda la
// pantalla). Lo usan el panel y los botones flotantes que se corren a su lado.
export const ANCHO_LATERAL = 440;
export const MEDIA_MOBILE = "@media (max-width: 767px)";

export function ProveedorContextoAsistente({ children }) {
  const [registros, setRegistros] = useState({});

  const valor = useMemo(() => {
    const pantalla =
      Object.values(registros).sort(
        (a, b) => PRIORIDAD[b.tipo] - PRIORIDAD[a.tipo]
      )[0] || null;
    return { pantalla, setRegistros };
  }, [registros]);

  return (
    <ContextoAsistente.Provider value={valor}>
      {children}
    </ContextoAsistente.Provider>
  );
}

/**
 * Informa al asistente la entidad que muestra la pantalla. Sin id no registra
 * nada (por ejemplo, mientras carga). El nombre es opcional.
 */
export function useContextoAsistente({ tipo, id, nombre }) {
  const contexto = useContext(ContextoAsistente);
  const setRegistros = contexto?.setRegistros;

  useEffect(() => {
    if (!setRegistros || !tipo || !id) return;
    const clave = tipo;
    setRegistros((prev) => ({
      ...prev,
      [clave]: { tipo, id: Number(id), ...(nombre && { nombre }) }
    }));
    return () =>
      setRegistros((prev) => {
        const { [clave]: _, ...resto } = prev;
        return resto;
      });
  }, [setRegistros, tipo, id, nombre]);
}

/** La entidad más específica que está en pantalla, o null. */
export function usePantallaActual() {
  return useContext(ContextoAsistente)?.pantalla || null;
}

// Evento de window para avisar que cambiaron los destacados del usuario (desde
// el botón estrella o una respuesta del asistente) y que el home los recargue.
export const EVENTO_DESTACADOS_ACTUALIZADOS = "morfeo:destacados-actualizados";

export function avisarDestacadosActualizados() {
  window.dispatchEvent(new Event(EVENTO_DESTACADOS_ACTUALIZADOS));
}
